/**
 * Fresh Product Form Handler
 * Handles variant selection, pricing, and discount code integration for fresh products
 */

(function () {
  'use strict';

  // UTF-8 safe Base64 encoding/decoding utilities
  const UTF8B64 = {
    encode(str) {
      const bytes = new TextEncoder().encode(str);
      let bin = '';
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      return btoa(bin);
    },
    decode(b64Like) {
      let raw = b64Like;
      try {
        raw = decodeURIComponent(b64Like);
      } catch (_) { }
      const bin = atob(raw);
      const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    },
  };

  /**
   * Main Product Form Handler
   */
  function productForm(config) {
    // VALUES
    let purchaseType = 'subscription';
    let currentVariant = config.currentVariant;
    let oneTimeVariant = config.oneTimeVariant;
    let currentCurrency = config.currentCurrency;
    let currentCountry = config.currentCountry;
    let globalDiscountPercentage = config.globalDiscountPercentage || 30;
    let currentRecipe = config.currentRecipe;
    let discount = {
      code: '',
      percentage: 1,
      amount: 0,
    };

    const selectors = {
      form: '#add-to-cart-form',
      purchaseType: '[name="purchase_type"]',
      variants: '[data-variants]',
      variantID: '[name="id"]',
      pension: '[name="pension"]',
      sellingPlan: '[name="selling_plan"]',
      variantPrice: '[data-variant-price]',
      subscriptionPrice: '[data-subscription-price]',
      oneTimePrice: '[data-one-time-variant-price]',
    };

    // ELEMENTS
    const formEl = document.querySelector(selectors.form);
    if (!formEl) return;

    const variantsEl = formEl.querySelector(selectors.variants);
    if (!variantsEl) return;
    const variants = JSON.parse(variantsEl.innerHTML);

    const variantInputEl = formEl.querySelector(selectors.variantID);
    const variantPriceEl = formEl.querySelector(selectors.variantPrice);
    const subscriptionPriceEl = formEl.querySelector(selectors.subscriptionPrice);
    const oneTimePriceEl = formEl.querySelector(selectors.oneTimePrice);

    const pensionNodeList = formEl.querySelectorAll(selectors.pension);
    const sellingPlanNodeList = formEl.querySelectorAll(selectors.sellingPlan);
    const purchaseTypeNodeList = formEl.querySelectorAll(selectors.purchaseType);

    const pensionElList = Array.from(pensionNodeList);
    const sellingPlanElList = Array.from(sellingPlanNodeList);
    const purchaseTypeElList = Array.from(purchaseTypeNodeList);

    /**
     * Format money based on country/currency
     */
    function formatMoney(number) {
      let currency = 'EUR';
      let locale = 'de-AT';

      if (currentCountry === 'CH') {
        currency = 'CHF';
        locale = 'de-CH';
      } else if (currentCountry === 'DE') {
        locale = 'de-DE';
      }

      let formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
      })
        .format(number)
        .replace('.', ',');

      if (currency !== 'CHF') {
        formatted = formatted.replace(/\s/g, '');
      }
      return formatted;
    }

    /**
     * Update current variant based on selections
     */
    function updateCurrentVariant() {
      const selectedPensionEl = pensionElList.find(function (el) {
        return el.checked;
      });

      const selectedCycleEl = sellingPlanElList.find(function (el) {
        return el.checked;
      });

      if (!selectedPensionEl || !selectedCycleEl) return;

      const selectedPension = selectedPensionEl.value;
      const selectedCycle = selectedCycleEl.dataset.value;

      const nextCurrentVariant = variants.find(function (variant) {
        return (
          variant.option1 === currentRecipe &&
          variant.option2 === selectedPension &&
          variant.option3 === selectedCycle
        );
      });

      const nextOneTimeVariant = oneTimeVariant
        ? variants.find(function (variant) {
          return (
            variant.option1 === currentRecipe &&
            variant.option2 === selectedPension &&
            variant.option3 === oneTimeVariant.option3
          );
        })
        : null;

      if (nextCurrentVariant) currentVariant = nextCurrentVariant;
      if (nextOneTimeVariant) oneTimeVariant = nextOneTimeVariant;
    }

    /**
     * Update form values
     */
    function updateFormValues() {
      if (purchaseType === 'subscription') {
        variantInputEl.value = currentVariant.id;
      } else if (oneTimeVariant) {
        variantInputEl.value = oneTimeVariant.id;
      }

      sellingPlanElList.forEach(function (sellingPlanEl) {
        sellingPlanEl.disabled = purchaseType === 'one_time';
      });
    }

    /**
     * Update price displays
     */
    function updatePrice() {
      if (variantPriceEl) {
        variantPriceEl.textContent = formatMoney(currentVariant.price / 100);
      }
      if (subscriptionPriceEl) {
        const discountPct = discount.amount || globalDiscountPercentage || 30;
        const multiplier = (100 - discountPct) / 100;
        subscriptionPriceEl.textContent = formatMoney(
          (currentVariant.selling_plan_allocations[0].price / 100) * multiplier
        );
      }
      if (oneTimePriceEl && oneTimeVariant) {
        oneTimePriceEl.textContent = formatMoney(oneTimeVariant.price / 100);
      }
    }

    /**
     * Update all values
     */
    function updateValues() {
      updateCurrentVariant();
      updateFormValues();
      updatePrice();
    }

    /**
     * Update selling plan active class
     */
    function updateSellingPlanActiveLoopClass() {
      sellingPlanElList.forEach(function (el) {
        const label = el.closest('label.loop-selling-plan-button');
        if (!label) return;
        if (el.checked && purchaseType === 'subscription') {
          label.classList.add('loop-selling-plan-button--selected');
        } else {
          label.classList.remove('loop-selling-plan-button--selected');
        }
      });
    }

    /**
     * Fetch discount from API
     */
    async function getDiscount() {
      const urlParams = new URLSearchParams(window.location.search);
      const discountCode = urlParams.get('discount');

      const host = window.location.hostname;
      const URL =
        host.includes('localhost') || host.includes('staging')
          ? 'https://petcon-api.dev.hellobello.at'
          : 'https://petcon-api.hellobello.at';

      if (discountCode) {
        try {
          const response = await fetch(`${URL}/api/v1/discount/${discountCode}`);
          const data = await response.json();
          if (data && data.data && data.data.discount) {
            discount = data.data.discount;
          }
        } catch (error) {
          console.log('Error caught while fetching the discount: ', error);
        }
      }

      // Use fallback to global_discount_percentage if discount.amount is empty/0
      const discountAmount = discount.amount || globalDiscountPercentage || 30;

      const discountCodeNodeList = document.querySelectorAll('[discount-code]');
      if (discountCodeNodeList.length && discountCode) {
        const discountCodeElList = Array.from(discountCodeNodeList);
        discountCodeElList.forEach(function (discountCodeEl) {
          discountCodeEl.textContent = discountCode;
        });
      }

      // Update all discount percentage elements
      setTimeout(function () {
        const discountCodePercentageNodeList = document.querySelectorAll('[discount-code-percentage]');
        if (discountCodePercentageNodeList.length) {
          const discountCodePercentageElList = Array.from(discountCodePercentageNodeList);
          discountCodePercentageElList.forEach(function (discountCodeEl) {
            discountCodeEl.textContent = discountAmount;
          });
        }
      }, 100);

      // Update discount object with fallback value for price calculations
      if (!discount.amount && globalDiscountPercentage) {
        discount.amount = globalDiscountPercentage;
      }
    }

    /**
     * Handle purchase type changes
     */
    purchaseTypeElList.forEach(function (purchaseTypeEl) {
      purchaseTypeEl.addEventListener('input', function () {
        purchaseType = purchaseTypeEl.value;

        if (purchaseType === 'one_time') {
          try {
            const dataRaw = localStorage.getItem('product_payload');
            if (dataRaw) {
              const data = JSON.parse(UTF8B64.decode(dataRaw));
              const props = data.items?.[0]?.properties;
              if (props) {
                props._shipping_interval_frequency = 0;
                localStorage.setItem('product_payload', UTF8B64.encode(JSON.stringify(data)));
                const freqInput = formEl.querySelector('input[name="properties[_shipping_interval_frequency]"]');
                if (freqInput) freqInput.value = 0;
              }
            }
          } catch (err) {
            console.error(err);
          }
        }

        if (purchaseType === 'subscription') {
          try {
            const dataRaw = localStorage.getItem('product_payload');
            if (dataRaw) {
              const data = JSON.parse(UTF8B64.decode(dataRaw));
              const props = data.items?.[0]?.properties;
              if (props) {
                props._shipping_interval_frequency = 4;
                localStorage.setItem('product_payload', UTF8B64.encode(JSON.stringify(data)));
                const freqInput = formEl.querySelector('input[name="properties[_shipping_interval_frequency]"]');
                if (freqInput) freqInput.value = 4;
              }
            }
          } catch (err) {
            console.error(err);
          }
        }

        updateValues();
      });
    });

    /**
     * Handle snack level — hide Halbpension when pet_snack_level is "lots"
     */
    function handleSnackLevel() {
      const snackInput = formEl.querySelector('input[name="properties[_pet_snack_level]"]');
      const snackValue = snackInput ? snackInput.value : '';
      const pensionContainer = formEl.querySelector('.fresh-variant-pension');

      if (snackValue === 'lots' && pensionContainer) {
        pensionContainer.classList.add('snack-level-lots');

        // Ensure Vollpension (V) is selected
        const vollpensionRadio = formEl.querySelector('input[name="pension"][value="V"]');
        if (vollpensionRadio && !vollpensionRadio.checked) {
          vollpensionRadio.checked = true;
          updateValues();
        }
      } else if (pensionContainer) {
        pensionContainer.classList.remove('snack-level-lots');
      }
    }

    /**
     * Handle pension changes
     */
    pensionElList.forEach(function (pensionEl) {
      pensionEl.addEventListener('input', updateValues);
    });

    /**
     * Handle selling plan changes
     */
    sellingPlanElList.forEach(function (sellingPlanEl) {
      sellingPlanEl.addEventListener('input', updateValues);
      sellingPlanEl.addEventListener('change', updateSellingPlanActiveLoopClass);
    });

    // Initialize (form has discount-pending from Liquid; add defensively if missing)
    (async function () {
      formEl.classList.add('discount-pending');
      try {
        await getDiscount();
        updatePrice();
      } finally {
        formEl.classList.remove('discount-pending');
      }
    })();

    updateSellingPlanActiveLoopClass();
    handleSnackLevel();
  }

  /**
   * Initialize form with product payload from localStorage
   */
  function init() {
    const formEl = document.querySelector('#add-to-cart-form');
    if (!formEl) return;

    // Check URL parameter first (for race condition prevention)
    const urlParams = new URLSearchParams(window.location.search);
    const urlPayload = urlParams.get('_i');
    let productPayload = localStorage.getItem('product_payload');

    if (urlPayload) {
      localStorage.setItem('product_payload', urlPayload);
      productPayload = urlPayload;
    }

    if (!productPayload) return;

    try {
      const payload = JSON.parse(UTF8B64.decode(productPayload));
      const properties = payload.items?.[0]?.properties;

      const selectedCycleEl = formEl.querySelector('input[name="selling_plan"]:checked');
      if (properties && selectedCycleEl) {
        properties._shipping_interval_frequency = parseInt(selectedCycleEl.dataset.value, 10) / 7;
      }

      if (!properties || Object.keys(properties).length === 0) return;

      formEl.querySelectorAll('input[name^="properties["]').forEach(function (input) {
        input.remove();
      });

      Object.entries(properties).forEach(function ([key, value]) {
        const inputEl = document.createElement('input');
        inputEl.type = 'hidden';
        inputEl.name = `properties[${key}]`;
        inputEl.value = value;
        formEl.appendChild(inputEl);
      });
    } catch (error) {
      return;
    }

    const container = formEl.querySelector('.loop-subscription-group-selling-plans-container');
    if (!container) return;

    const sellingPlanRadios = Array.from(container.querySelectorAll('input[name="selling_plan"]'));

    function updateSelectionUI() {
      sellingPlanRadios.forEach((radio) => {
        const label = radio.closest('.loop-selling-plan-button');
        label?.classList.toggle('loop-selling-plan-button--selected', radio.checked);
      });
    }

    container.addEventListener('input', (e) => {
      if (e.target.name !== 'selling_plan') return;
      updateSelectionUI();

      try {
        const dataRaw = localStorage.getItem('product_payload');
        if (!dataRaw) return;
        const data = JSON.parse(UTF8B64.decode(dataRaw));
        const freq = parseInt(e.target.dataset.value, 10);
        const weeks = freq / 7;

        data.items[0].properties._shipping_interval_frequency = weeks;
        localStorage.setItem('product_payload', UTF8B64.encode(JSON.stringify(data)));

        const freqInput = formEl.querySelector('input[name="properties[_shipping_interval_frequency]"]');
        if (freqInput) {
          freqInput.value = weeks;
        }
      } catch (err) {
        console.error(err);
      }
    });

    updateSelectionUI();
  }

  /**
   * Initialize on DOM ready
   */
  function initializeFreshProductForm() {
    const formEl = document.querySelector('#add-to-cart-form');
    if (!formEl) return;

    // Get config from data attributes
    const configEl = formEl.querySelector('[data-fresh-product-config]');
    if (!configEl) return;

    try {
      const config = JSON.parse(configEl.textContent);
      init();
      productForm(config);
    } catch (error) {
      console.error('Error initializing fresh product form:', error);
    }
  }

  // Export for global access
  window.FreshProductForm = {
    init: initializeFreshProductForm,
    UTF8B64: UTF8B64,
  };

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeFreshProductForm);
  } else {
    initializeFreshProductForm();
  }
})();

